import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import {
  getDeviceId, ensurePendingDevice, subscribeDeviceApproval, verifyDeviceCode,
} from '../service/deviceApprovalService';

const CODE_LEN = 4;

// 로그인했지만 미승인 기기일 때 앱 전체를 덮는 코드 입력 게이트.
// 관리자(isAdmin)는 호출 전에 면제 처리되므로 여기 오지 않음.
export default function DeviceApprovalGate({ children }) {
  const { user, logout } = useAuth();
  const [checking, setChecking] = useState(true);
  const [approved, setApproved] = useState(false);
  const [hasCode, setHasCode] = useState(false); // 관리자가 코드 생성했는지
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const deviceIdRef = useRef(getDeviceId());

  // 대기 등록 + 실시간 구독
  useEffect(() => {
    if (!user?.mcode) return;
    const deviceId = deviceIdRef.current;
    let unsub = null;
    ensurePendingDevice(deviceId, user.mcode, user.name).then((d) => {
      if (d?.status === 'approved') { setApproved(true); setChecking(false); return; }
      setHasCode(!!d?.code);
      setChecking(false);
      unsub = subscribeDeviceApproval(deviceId, (doc) => {
        if (!doc) return;
        if (doc.status === 'approved') setApproved(true);
        setHasCode(!!doc.code);
      });
    }).catch(() => { setApproved(true); setChecking(false); }); // 조회 실패 시 잠김 방지(통과)
    return () => { if (unsub) unsub(); };
  }, [user?.mcode, user?.name]);

  const submit = async (code) => {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await verifyDeviceCode(deviceIdRef.current, code);
      if (res.ok) { setApproved(true); return; }
      if (res.reason === 'no_code') setError('아직 관리자가 코드를 생성하지 않았습니다. 잠시만 기다려주세요.');
      else setError('코드가 일치하지 않습니다. 다시 입력해주세요.');
      setInput('');
    } catch (e) {
      setError('처리 중 오류가 발생했습니다.');
      setInput('');
    } finally {
      setSubmitting(false);
    }
  };

  const press = (n) => {
    setError('');
    setInput((prev) => {
      if (prev.length >= CODE_LEN) return prev;
      const next = prev + String(n);
      if (next.length === CODE_LEN) submit(next);
      return next;
    });
  };
  const back = () => { setError(''); setInput((p) => p.slice(0, -1)); };

  if (checking) return null;
  if (approved) return children;

  return (
    <Overlay>
      <Card>
        <Title>기기 인증이 필요합니다</Title>
        <Desc>
          이 기기는 처음 접속한 기기입니다.<br />
          관리자에게 <b>인증 코드</b>를 받아 입력해주세요.
        </Desc>
        <StaffLine>{user?.name || ''} 님 · {deviceIdRef.current.slice(0, 10)}…</StaffLine>

        <Dots>
          {Array.from({ length: CODE_LEN }).map((_, i) => (
            <Dot key={i} $filled={i < input.length} />
          ))}
        </Dots>

        {error && <ErrorMsg>{error}</ErrorMsg>}
        {!error && !hasCode && <Waiting>관리자 코드 생성 대기 중…</Waiting>}

        <Pad>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <Key key={n} onClick={() => press(n)} disabled={submitting}>{n}</Key>
          ))}
          <KeySpacer />
          <Key onClick={() => press(0)} disabled={submitting}>0</Key>
          <Key $sub onClick={back} disabled={submitting}>⌫</Key>
        </Pad>

        <LogoutBtn onClick={logout}>다른 계정으로 로그인</LogoutBtn>
      </Card>
    </Overlay>
  );
}

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 10000;
  background: #0F172A;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  font-family: 'Pretendard Variable', 'Pretendard Full', Pretendard, -apple-system, sans-serif;
`;
const Card = styled.div`
  width: 100%; max-width: 340px;
  display: flex; flex-direction: column; align-items: center;
`;
const Title = styled.div`font-size: 21px; font-weight: 800; color: #fff; margin-bottom: 10px;`;
const Desc = styled.div`font-size: 14px; line-height: 1.7; color: #CBD5E1; text-align: center; margin-bottom: 8px;`;
const StaffLine = styled.div`font-size: 12px; color: #64748B; margin-bottom: 22px;`;
const Dots = styled.div`display: flex; gap: 16px; margin-bottom: 16px;`;
const Dot = styled.div`
  width: 16px; height: 16px; border-radius: 50%;
  background: ${({ $filled }) => ($filled ? '#3B82F6' : 'transparent')};
  border: 2px solid ${({ $filled }) => ($filled ? '#3B82F6' : '#475569')};
  transition: all 0.12s;
`;
const ErrorMsg = styled.div`font-size: 13px; color: #F87171; margin-bottom: 14px; min-height: 18px;`;
const Waiting = styled.div`font-size: 13px; color: #FBBF24; margin-bottom: 14px; min-height: 18px;`;
const Pad = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
  width: 100%; max-width: 280px; margin-top: 6px;
`;
const Key = styled.button`
  height: 64px; border-radius: 14px;
  background: ${({ $sub }) => ($sub ? 'transparent' : 'rgba(255,255,255,0.08)')};
  color: #fff; font-size: ${({ $sub }) => ($sub ? '22px' : '26px')}; font-weight: 600;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background 0.12s;
  &:active { background: rgba(255,255,255,0.2); }
  &:disabled { opacity: 0.5; cursor: default; }
`;
const KeySpacer = styled.div``;
const LogoutBtn = styled.button`
  margin-top: 28px; padding: 8px 16px;
  background: transparent; color: #64748B; font-size: 13px; font-weight: 600;
  cursor: pointer;
  &:hover { color: #94A3B8; }
`;
